import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import DownloadIcon from '@mui/icons-material/Download';
import { getCommandLog, subscribeCommandLog, clearCommandLog } from '../../../utils/commandLog';

const STATUS_COLOR = {
  success: '#4CAF50',
  error: '#ff8892',
  suppressed: '#feec67',
  pending: 'rgba(255,255,255,0.4)',
};

const SOURCE_LABEL = {
  broker: 'BROKER',
  'event-service': 'EVENT',
};

const summarize = (entry) => {
  if (entry.source === 'broker') {
    const cap = entry.payload?.caption || {};
    return `${cap.action || '(no action)'}${cap.mode ? ` · ${cap.mode}` : ''} → ${entry.payload?.layer || '?'}`;
  }
  if (entry.source === 'event-service') {
    const p = entry.payload || {};
    return `${p.type || '?'}${p.event_op ? ` · ${p.event_op}` : ''}${p.goal_type ? ` (${p.goal_type})` : ''}`;
  }
  return entry.source || 'command';
};

const formatTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour12: false }) + `.${String(d.getMilliseconds()).padStart(3, '0')}`;
};

const formatEntryText = (entry) => {
  const status = entry.status || 'pending';
  const header = `[${entry.timestamp}] ${SOURCE_LABEL[entry.source] || entry.source} · ${status} — ${summarize(entry)}`;
  const body = JSON.stringify(entry.payload, null, 2);
  return `${header}\n${body}${entry.error ? `\nerror: ${entry.error}` : ''}\n`;
};

// The expanded view is a preview, not the artifact — maxHeight:300 means only
// a fraction is ever on screen, and Download already emits the full payload.
const MAX_JSON_CHARS = 4000;

const CommandRow = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const status = entry.status || 'pending';

  // Gated on `expanded` because MUI's Collapse keeps its children MOUNTED —
  // without this, every row re-stringifies its whole payload on every drawer
  // update, and subscribeCommandLog pushes a new array on each command, so
  // all ~100 rows re-render per command. Memoized as well as gated so an open
  // row doesn't re-stringify when an unrelated command lands.
  const body = useMemo(() => {
    if (!expanded) return null;
    const json = JSON.stringify(entry.payload, null, 2) ?? '';
    return json.length > MAX_JSON_CHARS
      ? { text: json.slice(0, MAX_JSON_CHARS), fullLength: json.length }
      : { text: json, fullLength: 0 };
  }, [expanded, entry.payload]);

  return (
    <Box
      sx={{
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        px: 1.5,
        py: 0.75,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
      }}
      onClick={() => setExpanded((v) => !v)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontFamily: 'monospace', minWidth: 96 }}>
          {formatTime(entry.timestamp)}
        </Typography>
        <Chip
          label={SOURCE_LABEL[entry.source] || entry.source}
          size="small"
          sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.08)', color: '#fff' }}
        />
        <Box
          sx={{
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: STATUS_COLOR[status] || STATUS_COLOR.pending,
            flexShrink: 0,
          }}
          title={status}
        />
        <Typography sx={{ color: '#fff', fontSize: 13, fontFamily: 'monospace', flex: 1 }} noWrap>
          {summarize(entry)}
        </Typography>
      </Box>
      {/* unmountOnExit so a collapsed row builds no DOM and runs no stringify
          at all — Collapse otherwise keeps every child mounted. */}
      <Collapse in={expanded} unmountOnExit>
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 1,
            p: 1,
            bgcolor: 'rgba(0,0,0,0.3)',
            borderRadius: 1,
            color: 'rgba(255,255,255,0.8)',
            fontSize: 11,
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            // 'anywhere' over 'break-all': breaks at natural boundaries first
            // and only falls back to mid-word on a line that actually
            // overflows, so the browser isn't weighing a break opportunity at
            // every character of a long JSON blob.
            overflowWrap: 'anywhere',
            maxHeight: 300,
            overflow: 'auto',
          }}
        >
          {body?.text}
          {body?.fullLength ? (
            <Box component="span" sx={{ color: '#feec67', fontStyle: 'italic' }}>
              {`\n\n… payload truncated at ${MAX_JSON_CHARS.toLocaleString()} of ${body.fullLength.toLocaleString()} chars — use Download for the full payload.`}
            </Box>
          ) : null}
          {entry.error ? `\n\nerror: ${entry.error}` : ''}
        </Box>
      </Collapse>
    </Box>
  );
};

CommandRow.propTypes = {
  entry: PropTypes.object.isRequired,
};

const CommandConsoleDrawer = ({ open, onClose }) => {
  const [entries, setEntries] = useState(() => getCommandLog());

  useEffect(() => {
    const unsubscribe = subscribeCommandLog(setEntries);
    return unsubscribe;
  }, []);

  const handleClear = useCallback((e) => {
    e.stopPropagation();
    clearCommandLog();
  }, []);

  const handleDownload = useCallback((e) => {
    e.stopPropagation();
    // Oldest-first in the file — natural top-to-bottom reading order, even
    // though the drawer itself lists newest-first.
    const text = [...entries].reverse().map(formatEntryText).join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aedrive-commands-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [entries]);

  return (
    // variant="persistent" — no Modal/backdrop, so the rest of the app stays
    // fully interactive while this is open (Chrome DevTools-style dock),
    // instead of the default "temporary" variant which traps focus/clicks.
    <Drawer
      variant="persistent"
      anchor="bottom"
      open={open}
      PaperProps={{
        sx: {
          height: 360,
          bgcolor: '#1c1c1c',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
          Command Console
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={handleDownload} title="Download as .txt" disabled={entries.length === 0}>
          <DownloadIcon sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 20 }} />
        </IconButton>
        <IconButton size="small" onClick={handleClear} title="Clear log">
          <DeleteSweepIcon sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 20 }} />
        </IconButton>
        <IconButton size="small" onClick={onClose} title="Close">
          <CloseIcon sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {entries.length === 0 ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, p: 2, textAlign: 'center' }}>
            No commands sent yet.
          </Typography>
        ) : (
          entries.map((entry) => <CommandRow key={entry.id} entry={entry} />)
        )}
      </Box>
    </Drawer>
  );
};

CommandConsoleDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CommandConsoleDrawer;
